import React, { useState, useEffect } from 'react';
import apiService from '../services/apiService';

function AdminMenu() {
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [menuFiles, setMenuFiles] = useState([]);
  const [menuPdf, setMenuPdf] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [activeTab, setActiveTab] = useState('items');
  const token = localStorage.getItem('token');

  useEffect(() => {
    loadMenu();
    loadCategories();
    loadMenuFiles();
    loadMenuPdf();
  }, []);

  const loadMenu = () => {
    apiService.getMenuItems()
      .then(response => setMenuItems(response.data.items))
      .catch(error => console.error('Error loading menu:', error));
  };

  const loadCategories = () => {
    apiService.getMenuCategories()
      .then(response => setCategories(response.data.categories))
      .catch(error => console.error('Error loading categories:', error));
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this menu item?')) {
      apiService.deleteMenuItem(id, token)
        .then(() => loadMenu())
        .catch(error => console.error('Error deleting menu item:', error));
    }
  };

  const loadMenuFiles = () => {
    apiService.getMenuFiles()
      .then(response => setMenuFiles(response.data.menu_files))
      .catch(error => console.error('Error loading menu files:', error));
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    setUploadProgress({});
    
    const results = {
      success: [],
      failed: []
    };

    // Upload files sequentially
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fileId = `${file.name}-${i}`;
      
      try {
        setUploadProgress(prev => ({
          ...prev,
          [fileId]: { status: 'uploading', fileName: file.name, progress: 0 }
        }));

        await apiService.uploadMenuFile(file, token);

        setUploadProgress(prev => ({
          ...prev,
          [fileId]: { status: 'completed', fileName: file.name, progress: 100 }
        }));

        results.success.push(file.name);
      } catch (error) {
        console.error('Error uploading file:', file.name, error);
        const errorMessage = error.response?.data?.message || error.message || 'Error uploading file';
        
        setUploadProgress(prev => ({
          ...prev,
          [fileId]: { status: 'failed', fileName: file.name, error: errorMessage, progress: 0 }
        }));

        results.failed.push({ name: file.name, error: errorMessage });
      }
    }

    // Reload menu files after all uploads
    loadMenuFiles();

    // Show summary
    let message = '';
    if (results.success.length > 0) {
      message += `Successfully uploaded ${results.success.length} file(s):\n${results.success.join(', ')}\n\n`;
    }
    if (results.failed.length > 0) {
      message += `Failed to upload ${results.failed.length} file(s):\n${results.failed.map(f => `${f.name}: ${f.error}`).join('\n')}`;
    }
    
    if (message) {
      alert(message);
    }

    // Clear progress after 3 seconds
    setTimeout(() => {
      setUploadProgress({});
    }, 3000);

    setUploading(false);
    e.target.value = ''; // Reset input
  };

  const handleDeleteFile = (id) => {
    if (window.confirm('Are you sure you want to delete this menu file?')) {
      apiService.deleteMenuFile(id, token)
        .then(() => loadMenuFiles())
        .catch(error => console.error('Error deleting menu file:', error));
    }
  };

  const handleReorder = (id, direction) => {
    const file = menuFiles.find(f => f.id === id);
    if (!file) return;

    const currentIndex = menuFiles.findIndex(f => f.id === id);
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

    if (newIndex < 0 || newIndex >= menuFiles.length) return;

    const otherFile = menuFiles[newIndex];
    
    // Swap display orders
    apiService.updateMenuFile({ id: file.id, display_order: otherFile.display_order }, token)
      .then(() => {
        return apiService.updateMenuFile({ id: otherFile.id, display_order: file.display_order }, token);
      })
      .then(() => loadMenuFiles())
      .catch(error => console.error('Error reordering:', error));
  };

  const loadMenuPdf = () => {
    apiService.getMenuPdf()
      .then(response => setMenuPdf(response.data.menu_pdf))
      .catch(error => console.error('Error loading menu PDF:', error));
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      alert('Please upload a PDF file');
      e.target.value = '';
      return;
    }

    setUploadingPdf(true);
    try {
      await apiService.uploadMenuPdf(file, token);
      loadMenuPdf();
      alert('Menu PDF uploaded successfully!');
      e.target.value = '';
    } catch (error) {
      console.error('Error uploading PDF:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Error uploading PDF';
      alert(`Error: ${errorMessage}`);
    } finally {
      setUploadingPdf(false);
    }
  };

  const handleDeletePdf = (id) => {
    if (window.confirm('Are you sure you want to delete this menu PDF?')) {
      apiService.deleteMenuPdf(id, token)
        .then(() => {
          setMenuPdf(null);
          alert('Menu PDF deleted successfully!');
        })
        .catch(error => console.error('Error deleting menu PDF:', error));
    }
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category ? category.name : 'N/A';
  };

  return (
    <div>
      <h2>Menu Management</h2>
      
      <div style={{marginBottom: '20px', borderBottom: '2px solid #ddd'}}>
        <button 
          className={`btn-admin ${activeTab === 'items' ? 'active' : ''}`}
          onClick={() => setActiveTab('items')}
          style={{marginRight: '10px', padding: '10px 20px', border: 'none', background: activeTab === 'items' ? '#2E8BC0' : '#f0f0f0', color: activeTab === 'items' ? 'white' : '#333', cursor: 'pointer'}}
        >
          Menu Items
        </button>
        <button 
          className={`btn-admin ${activeTab === 'files' ? 'active' : ''}`}
          onClick={() => setActiveTab('files')}
          style={{marginRight: '10px', padding: '10px 20px', border: 'none', background: activeTab === 'files' ? '#2E8BC0' : '#f0f0f0', color: activeTab === 'files' ? 'white' : '#333', cursor: 'pointer'}}
        >
          Menu Files
        </button>
        <button 
          className={`btn-admin ${activeTab === 'pdf' ? 'active' : ''}`}
          onClick={() => setActiveTab('pdf')}
          style={{padding: '10px 20px', border: 'none', background: activeTab === 'pdf' ? '#2E8BC0' : '#f0f0f0', color: activeTab === 'pdf' ? 'white' : '#333', cursor: 'pointer'}}
        >
          Menu PDF
        </button>
      </div>

      {activeTab === 'items' && (
      <div className="admin-card">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Category</th>
              <th>Price</th>
              <th>Available</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {menuItems.map(item => (
              <tr key={item.id}>
                <td>{item.name}</td>
                <td>{getCategoryName(item.category_id)}</td>
                <td>${item.price}</td>
                <td>{item.is_available ? 'Yes' : 'No'}</td>
                <td>
                  <button className="btn-admin btn-delete" onClick={() => handleDelete(item.id)}>
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {activeTab === 'files' && (
        <div>
          <div className="admin-card">
            <h3>Upload Menu File</h3>
            <div className="file-upload-area" onClick={() => document.getElementById('menuFileInput').click()}>
              <input
                id="menuFileInput"
                type="file"
                accept="image/*,.pdf"
                onChange={handleFileUpload}
                disabled={uploading}
                multiple
                style={{display: 'none'}}
              />
              <p style={{fontSize: '1.2rem', marginBottom: '10px'}}>
                {uploading ? 'Uploading...' : 'Click to upload menu file(s) (Image or PDF) - Multiple files supported'}
              </p>
              <p style={{color: '#999'}}>Supported formats: JPG, PNG, GIF, PDF (Max 10MB)</p>
              <p style={{color: '#666', fontSize: '0.9rem', marginTop: '5px'}}>You can select multiple files at once</p>
            </div>

            {/* Upload Progress */}
            {Object.keys(uploadProgress).length > 0 && (
              <div style={{marginTop: '20px', padding: '15px', background: '#f5f5f5', borderRadius: '4px'}}>
                <h4 style={{marginBottom: '10px'}}>Upload Progress:</h4>
                {Object.entries(uploadProgress).map(([fileId, progress]) => (
                  <div key={fileId} style={{marginBottom: '10px', padding: '10px', background: 'white', borderRadius: '4px'}}>
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px'}}>
                      <span style={{fontSize: '0.9rem', fontWeight: '500'}}>{progress.fileName}</span>
                      <span style={{
                        fontSize: '0.85rem',
                        color: progress.status === 'completed' ? '#28a745' : 
                               progress.status === 'failed' ? '#dc3545' : '#007bff'
                      }}>
                        {progress.status === 'uploading' && 'Uploading...'}
                        {progress.status === 'completed' && '✓ Completed'}
                        {progress.status === 'failed' && '✗ Failed'}
                      </span>
                    </div>
                    {progress.status !== 'failed' && (
                      <div style={{width: '100%', height: '6px', background: '#e0e0e0', borderRadius: '3px', overflow: 'hidden'}}>
                        <div style={{
                          width: `${progress.progress}%`,
                          height: '100%',
                          background: progress.status === 'completed' ? '#28a745' : '#007bff',
                          transition: 'width 0.3s'
                        }}></div>
                      </div>
                    )}
                    {progress.status === 'failed' && progress.error && (
                      <div style={{fontSize: '0.85rem', color: '#dc3545', marginTop: '5px'}}>
                        Error: {progress.error}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="admin-card">
            <h3>Menu Files ({menuFiles.length})</h3>
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '20px', marginTop: '20px'}}>
              {menuFiles.map((file, index) => (
                <div key={file.id} style={{position: 'relative', border: '1px solid #ddd', borderRadius: '4px', padding: '10px'}}>
                  {file.file_type === 'image' ? (
                    <img 
                      src={file.file_path} 
                      alt={file.file_name} 
                      style={{width: '100%', height: '200px', objectFit: 'cover', borderRadius: '4px'}}
                    />
                  ) : (
                    <div style={{width: '100%', height: '200px', background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '4px'}}>
                      <span style={{fontSize: '48px'}}>📄</span>
                    </div>
                  )}
                  <p style={{marginTop: '10px', fontSize: '0.9rem', wordBreak: 'break-word'}}>{file.file_name}</p>
                  <div style={{display: 'flex', gap: '5px', marginTop: '10px'}}>
                    <button 
                      className="btn-admin" 
                      onClick={() => handleReorder(file.id, 'up')}
                      disabled={index === 0}
                      style={{flex: 1, padding: '5px', fontSize: '12px'}}
                    >
                      ↑
                    </button>
                    <button 
                      className="btn-admin" 
                      onClick={() => handleReorder(file.id, 'down')}
                      disabled={index === menuFiles.length - 1}
                      style={{flex: 1, padding: '5px', fontSize: '12px'}}
                    >
                      ↓
                    </button>
                    <button 
                      className="btn-admin btn-delete" 
                      onClick={() => handleDeleteFile(file.id)}
                      style={{flex: 1, padding: '5px', fontSize: '12px'}}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
            {menuFiles.length === 0 && (
              <p style={{textAlign: 'center', color: '#999', marginTop: '20px'}}>No menu files uploaded yet.</p>
            )}
          </div>
        </div>
      )}

      {activeTab === 'pdf' && (
        <div>
          <div className="admin-card">
            <h3>Upload Menu PDF</h3>
            <div className="file-upload-area" onClick={() => document.getElementById('menuPdfInput').click()}>
              <input
                id="menuPdfInput"
                type="file"
                accept=".pdf"
                onChange={handlePdfUpload}
                disabled={uploadingPdf}
                style={{display: 'none'}}
              />
              <p style={{fontSize: '1.2rem', marginBottom: '10px'}}>
                {uploadingPdf ? 'Uploading...' : 'Click to upload menu PDF file'}
              </p>
              <p style={{color: '#999'}}>Supported format: PDF (Max 50MB)</p>
            </div>
          </div>

          <div className="admin-card">
            <h3>Current Menu PDF</h3>
            {menuPdf ? (
              <div style={{border: '1px solid #ddd', borderRadius: '4px', padding: '20px', marginTop: '20px'}}>
                <div style={{display: 'flex', alignItems: 'center', gap: '20px'}}>
                  <div style={{width: '80px', height: '80px', background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '4px'}}>
                    <span style={{fontSize: '48px'}}>📄</span>
                  </div>
                  <div style={{flex: 1}}>
                    <p style={{fontWeight: '600', marginBottom: '5px'}}>{menuPdf.file_name}</p>
                    <p style={{color: '#999', fontSize: '0.9rem'}}>Uploaded: {new Date(menuPdf.created_at).toLocaleDateString()}</p>
                  </div>
                  <button 
                    className="btn-admin btn-delete" 
                    onClick={() => handleDeletePdf(menuPdf.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ) : (
              <p style={{textAlign: 'center', color: '#999', marginTop: '20px'}}>No menu PDF uploaded yet.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminMenu;
